import React, { useMemo, useState } from "react";

import moment from "moment";

import {
  checkFeatureFlag,
  getEnvironment,
  isEmptyObject,
  isSingleFieldOutcomes,
  isSoilPlus3DataGapActiveForProgram,
  someOutcomeHasErrorOfType,
  useGetFarmPayments,
  useGetFarmReportingYear,
  useGetFarms,
  useGetFieldEvaluationMetricCorrections,
  useTranslation,
} from "utils";

import {
  CARBON_RUNNER_ERROR_TYPE,
  CarbonProgramOutcomesResponse,
  EVALUATION_METRICS,
  IMPACT_PANEL_CALCULATION_TYPE,
  IMPACT_PANEL_RESULT_TYPE,
  PRACTICE_SLUG,
  Payment,
  PracticeBundle,
  PracticeSlug,
  SingleFieldOutcomes,
  VIEW_TYPE,
  ValidAggregation,
} from "models";

import { Col, ExtraInfo, Flex, Icon, IconLinkButton, LoadingOverlay, Option, Select, Typography } from "components";

import { ImpactPanelCollapseToggle } from "pages/app/all-field-view/components/CollapsibleImpactPanel";
import { SubmissionSuccessModalContent } from "pages/app/all-field-view/components/SubmissionSuccessModal";
import { getFirstUnlockedYear } from "pages/app/all-field-view/utils/dataEntryUtils";
import { EvaluationMetricsPanel } from "pages/app/field-timeline/components/EvaluationMetricsPanel";
import { FieldTimelineResultCard } from "pages/app/field-timeline/components/FieldTimelineResultCard";
import { MissingDataGuidance } from "pages/app/field-timeline/components/MissingDataGuidance";
import { calculateImpactPanelBodyHeight } from "pages/app/field-timeline/utils/TimelineUtils";

type FieldTimelineImpactPanelProps = {
  carbonProgramOutcomes: CarbonProgramOutcomesResponse;
  progress: number;
  viewType: VIEW_TYPE;
  fieldSizes: Record<string, number>;
  hasMissingData?: boolean;
  setNavigateToMissingData?: (value: boolean) => void;
  practiceBundles?: PracticeBundle[];
  shouldShowSubmissionSuccessModalContent?: boolean;
};

// This function is used to filter carbon program outcomes to show only current and future years,
// excluding historical data that may not be relevant for current impact calculations.
export const filterFieldOutcomesByYear = (data: CarbonProgramOutcomesResponse, minYear: number = moment().year()) =>
  Object.entries(data).reduce((filteredData, [fieldId, field]) => {
    const singleField = field as SingleFieldOutcomes;
    if (!singleField?.fieldOutcomes) {
      return { ...filteredData, [fieldId]: singleField };
    }

    const filteredPractices = Object.entries(singleField.fieldOutcomes).reduce((practices, [practiceSlug, practice]) => {
      if (!practice || typeof practice !== "object" || !("years" in practice) || !practice.years) {
        return practices;
      }

      const validYears = Object.entries(practice.years)
        .filter(([year]) => Number(year) >= minYear)
        .reduce(
          (yearsAccumulator, [year, yearData]) => ({
            ...yearsAccumulator,
            [year]: yearData,
          }),
          {}
        );

      return {
        ...practices,
        [practiceSlug]: {
          ...practice,
          years: validYears,
        },
      };
    }, {});

    return {
      ...filteredData,
      [fieldId]: {
        ...singleField,
        fieldOutcomes: filteredPractices,
      },
    };
  }, {} as CarbonProgramOutcomesResponse);

export const InterventionImpactPanel = ({
  carbonProgramOutcomes,
  progress,
  fieldSizes,
  viewType,
  hasMissingData = false,
  setNavigateToMissingData,
  practiceBundles,
}: FieldTimelineImpactPanelProps) => {
  const { t } = useTranslation(["fieldTimeline"]);
  const env = getEnvironment();
  const areEvaluationMetricsEnabled = env.production ? checkFeatureFlag("evaluation-metrics") : true;

  const [showPayoutDrawer, setShowPayoutDrawer] = useState(false);
  const [selectedPanel, setSelectedPanel] = useState<"payout" | EVALUATION_METRICS>("payout");

  const [calculationType, setCalculationType] = useState<IMPACT_PANEL_CALCULATION_TYPE>(IMPACT_PANEL_CALCULATION_TYPE.TOTAL);
  const { currentFarm } = useGetFarms();

  const isSoilPlus3DataGapActive = isSoilPlus3DataGapActiveForProgram(currentFarm?.carbonProgram);
  const {
    data: { activeFarmReportingYear = moment().year(), isReportingYearLocked },
  } = useGetFarmReportingYear(currentFarm?.id || null);
  const firstUnlockedYear = getFirstUnlockedYear(activeFarmReportingYear, isReportingYearLocked);

  const carbonProgramOutcomesUpFromFirstUnlockedYear = filterFieldOutcomesByYear(carbonProgramOutcomes, firstUnlockedYear);
  const bodyHeight = calculateImpactPanelBodyHeight();

  const singleFieldOutcomes: SingleFieldOutcomes[] = Object.values(carbonProgramOutcomesUpFromFirstUnlockedYear).filter(isSingleFieldOutcomes);

  // We only need to fetch past payments for the all fields view
  const isAllFieldsView = viewType === VIEW_TYPE.ALL_FIELDS;

  const { data: farmPayments } = useGetFarmPayments(isAllFieldsView);
  const pastPayouts = isAllFieldsView ? groupPaymentsByYear(farmPayments?.payments.filter(({ year }) => year < firstUnlockedYear) ?? []) : [];

  const { years, allYearsLabel } = generateYearLabels(singleFieldOutcomes, pastPayouts);
  const [selectedYear, setSelectedYear] = useState<number | string>(moment().year());

  const calculationTypeOptions = [
    { label: t("impactPanel.total"), value: IMPACT_PANEL_CALCULATION_TYPE.TOTAL },
    { label: t("impactPanel.perHa"), value: IMPACT_PANEL_CALCULATION_TYPE.PER_HA },
  ];

  const fieldIds = Object.keys(carbonProgramOutcomes);
  const practiceSlugs = fieldIds
    .map((fieldId) => {
      const fieldCarbonProgramOutcomes = carbonProgramOutcomes[fieldId];
      if ("error" in fieldCarbonProgramOutcomes) return [];

      return Object.keys(fieldCarbonProgramOutcomes.fieldOutcomes);
    })
    .flat() as PracticeSlug[];
  const uniquePracticeSlugs = Array.from(new Set(practiceSlugs));

  const panelMetrics = areEvaluationMetricsEnabled ? ["payout", ...Object.values(EVALUATION_METRICS)] : ["payout"];

  const { data: existingCorrectionsByFieldId } = useGetFieldEvaluationMetricCorrections({ fieldIds: areEvaluationMetricsEnabled ? fieldIds : [] });

  const shouldShowLastSubmittedYearPayoutEstimation =
    Number(selectedYear) === activeFarmReportingYear &&
    isReportingYearLocked &&
    pastPayouts.some((payment) => payment.year === Number(selectedYear) && payment.status !== "paid");

  const hasErrorData = useMemo(
    () => someOutcomeHasErrorOfType(carbonProgramOutcomes ?? {}, CARBON_RUNNER_ERROR_TYPE.FOOTPRINT_SERVICE),
    [carbonProgramOutcomes]
  );

  return !activeFarmReportingYear ? null : (
    <Flex className="field-timeline-impact-panel container" data-testid="field-timeline-impact-panel" vertical={true}>
      <div className="field-timeline-impact-panel body" style={{ height: `${bodyHeight}rem` }}>
        <Flex vertical={true}>
          <Flex align="center" justify="space-between" className="margin-bottom-100">
            <Flex align="center">
              <Select
                className="panel-select"
                dataTestId="panel-select"
                name="panelSelect"
                popupMatchSelectWidth={false}
                onChange={setSelectedPanel}
                defaultValue={selectedPanel}
                value={selectedPanel}
              >
                {panelMetrics.map((metric, i) => (
                  <Option data-testid={`panel-select-option-${i}`} value={metric} key={i} className="panel-select-option">
                    {metric === "payout" ? t("impactPanel.payout") : t(`impactPanel.evaluationMetrics.${metric}`)}
                  </Option>
                ))}
              </Select>
              {selectedPanel === "payout" && (
                <IconLinkButton
                  dataTestId="field-timeline-payout-info-icon-button"
                  className="field-timeline-payout-info-icon-button"
                  theme="grey"
                  iconType="info"
                  size="small"
                  onClick={() => setShowPayoutDrawer(true)}
                />
              )}
            </Flex>
            <ImpactPanelCollapseToggle />
          </Flex>
          {!!years.length && selectedPanel === "payout" && !hasErrorData && !hasMissingData && (
            <>
              <Flex justify="space-between" align="center">
                <Select
                  key={selectedYear}
                  className="payout-years-select"
                  dataTestId="payout-years-select"
                  name="payoutYears"
                  popupMatchSelectWidth={false}
                  onChange={(value) => {
                    setSelectedYear(value);
                    // if the selected year is before the reporting year, we set the view option to total since the per ha option is going to be disabled
                    if (Number(value) < activeFarmReportingYear) setCalculationType(IMPACT_PANEL_CALCULATION_TYPE.TOTAL);
                  }}
                  defaultValue={selectedYear}
                  value={selectedYear}
                >
                  {/* TODO: check if we want to re-instate the all years label */}
                  {years.map((year, i) => (
                    <Option data-testid={`payout-year-select-option-${i}`} value={year} key={i} className="payout-years-select-option">
                      <Flex align="center">
                        {year}
                        {Number(year) === activeFarmReportingYear ? (
                          <div data-testid="reporting-year-label" className="reporting-year-label">
                            {t("reportingYear")}
                          </div>
                        ) : null}
                      </Flex>
                    </Option>
                  ))}
                </Select>
                {/* Only show per ha option if current reporting year or future year is selected, because past payouts might have different farm sizes, which would lead to a lot more complexity in the code */}
                <Select
                  // add the value as key to force a re-render when the value is programmatically updated by the year selection (per ha not allowed for past years)
                  key={calculationType}
                  className="field-timeline-result-select"
                  dataTestId="field-timeline-result-select"
                  name="resultSelect"
                  popupMatchSelectWidth={false}
                  onChange={setCalculationType}
                  defaultValue={calculationType}
                  value={calculationType}
                >
                  {calculationTypeOptions.map((option, i) => (
                    <Option
                      data-testid={`field-timeline-result-select-option-${i}`}
                      value={option.value}
                      key={i}
                      disabled={option.value === IMPACT_PANEL_CALCULATION_TYPE.PER_HA && Number(selectedYear) < activeFarmReportingYear}
                      className="field-timeline-result-select-option"
                    >
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Flex>
              <span className="dotted-line-divider" />
            </>
          )}
          {progress === 100 ? (
            <>
              {shouldShowLastSubmittedYearPayoutEstimation ? (
                <SubmissionSuccessModalContent carbonProgramOutcomes={carbonProgramOutcomes ?? null} farmId={currentFarm?.id ?? ""} variant="panel" />
              ) : selectedPanel === "payout" ? (
                // TODO: remove after soil+ 3.0 release, intentionally hardcoded 2025 because it's the last year before the introduction of the RothC model introduction to calculate payouts
                isSoilPlus3DataGapActive && Number(selectedYear) > 2025 ? (
                  <div className="field-timeline-result-card">
                    <div className="field-timeline-result-card container empty">
                      <Flex vertical align="center" justify="center" style={{ height: "100%" }}>
                        <Icon className="margin-bottom-100" iconName="klim/funding" size="xl" />
                        <Typography variant="paragraph-m" className="field-timeline-result-card-empty-title">
                          {t("impactPanel.payoutComingSoonTitle", { year: selectedYear === allYearsLabel ? "" : selectedYear })}
                        </Typography>
                        <Typography variant="paragraph-m" className="field-timeline-result-card-empty-payout-description">
                          {t("impactPanel.payoutComingSoonDescription")}
                        </Typography>
                      </Flex>
                    </div>
                  </div>
                ) : (
                  <FieldTimelineResultCard
                    resultType={selectedYear !== allYearsLabel ? IMPACT_PANEL_RESULT_TYPE.YEARLY : IMPACT_PANEL_RESULT_TYPE.ALL_YEARS}
                    calculationType={calculationType}
                    singleFieldOutcomes={singleFieldOutcomes}
                    showResults={!!years.length}
                    selectedYear={selectedYear === allYearsLabel ? selectedYear : Number(selectedYear)}
                    yearRange={years.map(Number)}
                    reportingYear={activeFarmReportingYear}
                    practiceSlugs={uniquePracticeSlugs}
                    fieldSizes={fieldSizes}
                    viewType={viewType}
                    hasMissingData={hasMissingData}
                    hasErrorData={hasErrorData}
                    setNavigateToMissingData={setNavigateToMissingData}
                    practiceBundles={practiceBundles}
                    pastPayouts={pastPayouts}
                  />
                )
              ) : (
                <EvaluationMetricsPanel
                  selectedMetric={selectedPanel}
                  carbonProgramOutcomes={carbonProgramOutcomes}
                  reportingYear={activeFarmReportingYear}
                  fieldSizes={fieldSizes}
                  viewType={viewType}
                  existingCorrectionsByFieldId={existingCorrectionsByFieldId ?? {}}
                  reportingPeriod={currentFarm?.carbonProgram?.fieldsManagement.reportingPeriod}
                />
              )}
              {/* Do not show guidance for past years */}
              {isAllFieldsView && !hasErrorData && selectedPanel === "payout" && Number(selectedYear) > activeFarmReportingYear && <MissingDataGuidance />}
            </>
          ) : (
            <Flex align="center" justify="center" vertical>
              <LoadingOverlay>
                <Col className="loading-message">
                  <Typography variant="paragraph-s">{t("impactPanel.fieldsCalculated")}</Typography>
                </Col>
              </LoadingOverlay>
            </Flex>
          )}
        </Flex>
      </div>
      <ExtraInfo setVisible={setShowPayoutDrawer} type="payout-overview" visible={showPayoutDrawer} />
    </Flex>
  );
};

const generateYearLabels = (singleFieldOutcomes: SingleFieldOutcomes[], pastPayouts: Payment[]) => {
  const fieldOutcomeYears = singleFieldOutcomes.flatMap(({ fieldOutcomes }) => {
    if (isEmptyObject(fieldOutcomes)) return [];

    const calculationsPerPractice = fieldOutcomes[PRACTICE_SLUG.CROPS];
    if (!calculationsPerPractice || isEmptyObject(calculationsPerPractice)) return [];

    const yearsCalculations = calculationsPerPractice.years;
    if (isEmptyObject(yearsCalculations)) return [];

    // numbers like the past payout years, so the default selected year matches its option
    return Object.keys(yearsCalculations)
      .filter((year) => {
        const yearlyOutcomesPerPractice = yearsCalculations[year];
        return (
          yearlyOutcomesPerPractice &&
          !yearlyOutcomesPerPractice.isBaselineYear &&
          yearlyOutcomesPerPractice.validation?.valid &&
          typeof (yearlyOutcomesPerPractice as ValidAggregation)?.payouts?.avg === "number"
        );
      })
      .map(Number);
  });

  const pastYears = pastPayouts.map(({ year }) => year);

  const years = Array.from(new Set([...pastYears, ...fieldOutcomeYears])).sort();

  const allYearsLabel = years.length ? `${years[0]} - ${years[years.length - 1]}` : "";
  return { years, allYearsLabel };
};

const groupPaymentsByYear = (payments: Payment[]) =>
  payments.reduce((acc, payment) => {
    const existingPayment = acc.find((p) => p.year === payment.year);
    if (existingPayment) {
      existingPayment.amount += payment.amount;
    } else {
      acc.push({ ...payment });
    }
    return acc;
  }, [] as Payment[]);
